/**
 * Build NewsCard chunk data from overnight Firestore translations (no backend re-translate).
 */
export function buildNewsCardTranslationData(news, selectedLanguage) {
  const en = news.translations?.en || {};
  const titleEn = String(en.title ?? news.title ?? "").trim();
  const summaryEn = String(en.summary ?? news.summary ?? "").trim();
  const titleEnChunks = normalizeChunks(en.titleChunks, titleEn);
  const summaryEnChunks = normalizeChunks(en.summaryChunks, summaryEn);

  if (!selectedLanguage || selectedLanguage === "en") {
    return {
      originalSummaryChunks: summaryEnChunks,
      translatedSummaryChunks: summaryEnChunks,
      originalTitleChunks: titleEnChunks,
      translatedTitleChunks: titleEnChunks,
    };
  }

  const lang = news.translations?.[selectedLanguage] || {};
  const titleLang = String(lang.title ?? "").trim() || titleEn;
  const summaryLang = String(lang.summary ?? "").trim() || summaryEn;
  const titleLangChunks = normalizeChunks(lang.titleChunks, titleLang);
  const summaryLangChunks = normalizeChunks(lang.summaryChunks, summaryLang);

  return {
    originalTitleChunks: alignedOriginalChunks(titleEnChunks, titleLangChunks, titleEn),
    translatedTitleChunks: alignedTranslatedChunks(titleEnChunks, titleLangChunks, titleLang),
    originalSummaryChunks: alignedOriginalChunks(summaryEnChunks, summaryLangChunks, summaryEn),
    translatedSummaryChunks: alignedTranslatedChunks(summaryEnChunks, summaryLangChunks, summaryLang),
  };
}

function normalizeChunks(chunks, fallbackFull) {
  if (!Array.isArray(chunks)) return [fallbackFull];
  const cleaned = chunks.map((c) => String(c ?? "").trim()).filter(Boolean);
  return cleaned.length ? cleaned : [fallbackFull];
}

/** Prefer stored aligned chunk arrays; fall back to full text when lengths mismatch (legacy docs). */
function alignedOriginalChunks(enChunks, langChunks, fullEn) {
  if (enChunks.length > 1 && enChunks.length === langChunks.length) return enChunks;
  return [fullEn];
}

function alignedTranslatedChunks(enChunks, langChunks, fullLang) {
  if (enChunks.length > 1 && enChunks.length === langChunks.length) return langChunks;
  return [fullLang];
}

/** Same token normalization as puzzle slots (gameTextSanitize.normalizeEnglishArticleToken). */
function gameWordKey(englishWord) {
  const s = String(englishWord || "").trim();
  if (!s) return "";
  return s
    .replace(/^[^a-zA-Z]+/g, "")
    .replace(/[^a-zA-Z]+$/g, "")
    .toLowerCase();
}

/** @param {{ word: string, translations?: Record<string, string> }[]} gameWords */
export function buildGameWordTranslationMap(gameWords, selectedLanguage) {
  const map = {};
  if (!selectedLanguage || selectedLanguage === "en" || !gameWords?.length) {
    return map;
  }
  for (const gw of gameWords) {
    const raw = String(gw.word || gw.translations?.en || "").trim();
    const key = gameWordKey(raw);
    const t = gw.translations?.[selectedLanguage];
    if (key && t != null && String(t).trim()) {
      map[key] = String(t).trim();
    }
  }
  return map;
}

export function lookupGameWordTranslation(map, englishWord) {
  const key = gameWordKey(englishWord);
  return key ? map[key] ?? null : null;
}

/** Side-panel / clue label from Firestore — never calls the backend. */
export function resolveGameWordLabel(englishWord, selectedLanguage, map) {
  const w = String(englishWord || "").trim();
  const original = w.toUpperCase();
  if (!w) return { original: "", translated: "" };
  if (!selectedLanguage || selectedLanguage === "en") {
    return { original, translated: original };
  }
  const stored = lookupGameWordTranslation(map, w);
  if (stored) {
    return { original, translated: stored };
  }
  return { original, translated: original };
}

/** @param {string[]} englishWords */
export function resolveGameWordLabels(englishWords, selectedLanguage, map, sanitize) {
  return (englishWords || [])
    .map((w) => String(w || "").trim())
    .filter(Boolean)
    .map((word) => {
      const { original, translated } = resolveGameWordLabel(word, selectedLanguage, map);
      const display = sanitize ? sanitize(translated, original) : translated;
      return { original, translated: display };
    });
}
